import { useState } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { LogOut, Camera, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LiveDurationCounter } from './LiveDurationCounter';
import { MethodIcon } from './MethodIcon';

function getInitials(checkIn) {
  const first = checkIn.first_name || checkIn.display_name || '';
  const last = checkIn.last_name || '';
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  return '??';
}

function getDisplayName(checkIn) {
  if (checkIn.display_name) return checkIn.display_name;
  const parts = [checkIn.first_name, checkIn.last_name].filter(Boolean);
  return parts.join(' ') || 'Unknown';
}

function formatClockIn(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getComplianceDotColor(stats) {
  if (!stats || stats.compliancePercentage == null) return 'bg-gray-300';
  if (stats.compliancePercentage >= 80) return 'bg-emerald-500';
  if (stats.compliancePercentage >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

export function OnSiteEmployeeRow({
  checkIn,
  employeeStats,
  statsLoading,
  onCheckOut,
  onViewSelfie,
}) {
  const [checkingOut, setCheckingOut] = useState(false);
  const initials = getInitials(checkIn);
  const name = getDisplayName(checkIn);
  const department = checkIn.department || checkIn.job_title || '';

  return (
    <div className="group hover:bg-muted/50 transition-colors px-4 py-3 flex items-center gap-3">
      {/* Avatar */}
      <Avatar className="h-9 w-9 shrink-0">
        {checkIn.avatar_url && <AvatarImage src={checkIn.avatar_url} alt={name} />}
        <AvatarFallback className="bg-gradient-to-br from-emerald-400 to-emerald-600 text-white text-xs font-medium">
          {initials}
        </AvatarFallback>
      </Avatar>

      {/* Name + Department */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground truncate">{name}</p>
        {department && (
          <p className="text-xs text-muted-foreground truncate">{department}</p>
        )}
      </div>

      {/* Clock-in time */}
      <span className="text-xs text-muted-foreground shrink-0 hidden sm:inline">
        {formatClockIn(checkIn.check_in_time)}
      </span>

      {/* Live duration */}
      <LiveDurationCounter
        startTime={checkIn.check_in_time}
        className="shrink-0 hidden sm:inline"
      />

      {/* Method icon */}
      <MethodIcon method={checkIn.check_in_method} className="shrink-0" />

      {/* Selfie thumbnail */}
      {checkIn.selfie_url && (
        <button
          onClick={() => onViewSelfie?.(checkIn.selfie_url)}
          className="shrink-0 overflow-hidden rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
        >
          <img
            src={checkIn.selfie_url}
            alt="Selfie"
            className="w-8 h-8 rounded-md object-cover"
          />
        </button>
      )}

      {/* Compliance dot */}
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'w-2.5 h-2.5 rounded-full shrink-0',
              statsLoading ? 'bg-gray-200 animate-pulse' : getComplianceDotColor(employeeStats)
            )}
          />
        </TooltipTrigger>
        <TooltipContent>
          {statsLoading ? (
            <p>Loading compliance...</p>
          ) : employeeStats?.compliancePercentage != null ? (
            <p>
              {employeeStats.compliancePercentage}% compliant ({employeeStats.valid}/
              {employeeStats.totalAssigned})
            </p>
          ) : (
            <p>No compliance data</p>
          )}
        </TooltipContent>
      </Tooltip>

      {/* Check Out button */}
      <Button
        variant="ghost"
        size="sm"
        disabled={checkingOut}
        onClick={async () => {
          setCheckingOut(true);
          try {
            await onCheckOut?.(checkIn.id);
          } finally {
            setCheckingOut(false);
          }
        }}
        className={cn(
          "shrink-0 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50 h-8 px-2 gap-1",
          checkingOut ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        )}
      >
        {checkingOut ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <LogOut className="h-3.5 w-3.5" />
        )}
        <span className="text-xs">{checkingOut ? 'Checking out...' : 'Out'}</span>
      </Button>
    </div>
  );
}
